"use client";

import { useEffect, useRef, useState } from "react";
import { CodeXml, Eye, Loader2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { readSkillError } from "@/components/command-skills/skill-api";
import {
  codeLanguageForPath,
  formatBytes,
  isMarkdownPath,
  validateSkillInstructions,
} from "@/components/command-skills/skill-utils";
import { useSkillFile } from "@/hooks/use-command-skills";
import { CodeBlock, CodeBlockCopyButton } from "@/components/ui/shadcn-io/ai/code-block";
import { Response } from "@/components/ui/shadcn-io/ai/response";
import { cn } from "@/lib/utils";
import type { SkillDetail } from "@/types/command-skill";

interface SkillFileViewerProps {
  skill: SkillDetail;
  path: string;
  saving?: boolean;
  onSaveInstructions: (instructions: string) => Promise<void>;
  onSaveFile: (path: string, file: File) => Promise<void>;
}

type FileView = "formatted" | "code";

export function SkillFileViewer({
  skill,
  path,
  saving = false,
  onSaveInstructions,
  onSaveFile,
}: SkillFileViewerProps) {
  const file = skill.files.find((item) => item.path === path);
  const isSkillMd = path === "SKILL.md";
  const shouldFetch = !isSkillMd && Boolean(file?.is_text);
  const query = useSkillFile(skill.id, path, shouldFetch);
  const queryClient = useQueryClient();
  const toasted = useRef<string | null>(null);
  const [view, setView] = useState<FileView>("formatted");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [editError, setEditError] = useState<string | null>(null);

  const source = isSkillMd ? skill.instructions : (query.data?.content ?? null);
  const isText = isSkillMd || Boolean(query.data ? query.data.is_text : file?.is_text);
  const markdown = isSkillMd || isMarkdownPath(path);
  const canEdit = skill.permissions.can_edit && isText && source !== null;

  useEffect(() => {
    setView(markdown ? "formatted" : "code");
    setEditing(false);
    setDraft("");
    setEditError(null);
  }, [markdown, path]);

  useEffect(() => {
    if (!query.error) return;
    const key = `${path}:${readSkillError(query.error).code ?? "error"}`;
    if (toasted.current === key) return;
    toasted.current = key;
    const parsed = readSkillError(query.error, "Could not open this file");
    if (parsed.code === "COMMAND_TEAM__SKILL_FILE_NOT_FOUND") {
      toast.error(parsed.message);
      void queryClient.invalidateQueries({ queryKey: ["skill", skill.id] });
      return;
    }
    if (parsed.code !== "COMMAND_TEAM__SKILL_NOT_FOUND") {
      toast.error(parsed.message);
    }
  }, [path, query.error, queryClient, skill.id]);

  const startEdit = () => {
    if (source === null) return;
    setDraft(source);
    setEditError(null);
    setEditing(true);
    setView("code");
  };

  const save = async () => {
    if (isSkillMd) {
      const instructionsError = validateSkillInstructions(draft);
      if (instructionsError) {
        setEditError(instructionsError);
        return;
      }
      try {
        await onSaveInstructions(draft);
        setEditing(false);
      } catch (error) {
        setEditError(readSkillError(error, "Could not save this file").message);
      }
      return;
    }
    if (!draft.trim() && draft.length === 0) {
      setEditError("File cannot be empty");
      return;
    }
    const filename = path.split("/").pop() || "file";
    try {
      await onSaveFile(path, new File([draft], filename, { type: "text/plain" }));
      setEditing(false);
    } catch (error) {
      setEditError(readSkillError(error, "Could not save this file").message);
    }
  };

  if (!file && !isSkillMd) {
    return <p className="px-4 py-6 text-sm text-muted-foreground">Select a file to view it.</p>;
  }

  const shown = editing ? draft : (source ?? "");

  return (
    <div className="min-w-0 max-w-full px-4 pb-6 sm:px-5">
      <div className="sticky top-0 z-10 -mx-4 mb-4 flex items-center justify-between gap-3 border-b border-border/70 bg-background px-4 py-2.5 sm:-mx-5 sm:px-5">
        <h2 className="truncate font-mono text-[13px] text-muted-foreground">/{path}</h2>
        <div className="flex shrink-0 items-center gap-2">
          {isText && source !== null ? (
            <div className="flex items-center rounded-lg border border-border p-0.5">
              <button
                type="button"
                aria-label="Formatted"
                aria-pressed={view === "formatted"}
                onClick={() => setView("formatted")}
                className={cn(
                  "rounded-md p-1.5 text-muted-foreground hover:text-foreground",
                  view === "formatted" && "bg-muted text-foreground",
                )}
              >
                <Eye className="size-3.5" />
              </button>
              <button
                type="button"
                aria-label="Code"
                aria-pressed={view === "code"}
                onClick={() => setView("code")}
                className={cn(
                  "rounded-md p-1.5 text-muted-foreground hover:text-foreground",
                  view === "code" && "bg-muted text-foreground",
                )}
              >
                <CodeXml className="size-3.5" />
              </button>
            </div>
          ) : null}
          {canEdit && !editing ? (
            <button
              type="button"
              onClick={startEdit}
              className="text-[13px] font-medium text-foreground hover:underline"
            >
              Edit
            </button>
          ) : null}
          {editing ? (
            <>
              <button
                type="button"
                disabled={saving}
                onClick={() => {
                  setEditing(false);
                  setEditError(null);
                }}
                className="text-[13px] font-medium text-muted-foreground hover:text-foreground"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => void save()}
                className="text-[13px] font-medium text-foreground hover:underline disabled:opacity-60"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </>
          ) : null}
        </div>
      </div>

      {!isSkillMd && query.isLoading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Opening file…
        </div>
      ) : !isText || source === null ? (
        <div className="rounded-2xl border border-dashed px-6 py-10 text-center">
          <p className="font-mono text-sm font-medium">{path.split("/").pop()}</p>
          <p className="mt-1 text-xs text-muted-foreground">{formatBytes(file?.size ?? 0)}</p>
          <p className="mx-auto mt-3 max-w-sm text-sm text-muted-foreground">
            Binary file. Included in the skill download and available to the skill&apos;s scripts.
          </p>
        </div>
      ) : view === "formatted" && markdown ? (
        shown.trim() ? (
          <Response>{shown}</Response>
        ) : (
          <p className="text-sm text-muted-foreground">This file is empty.</p>
        )
      ) : view === "code" && editing ? (
        <textarea
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
            setEditError(null);
          }}
          spellCheck={false}
          className="min-h-[24rem] w-full resize-y rounded-xl border border-border bg-muted/30 p-4 font-mono text-[13px] leading-relaxed outline-none focus:border-foreground/30"
        />
      ) : (
        <SourceView path={path} content={shown} highlighted={view === "formatted"} />
      )}
      {editError ? <p className="mt-3 text-sm text-destructive">{editError}</p> : null}
    </div>
  );
}

function SourceView({
  path,
  content,
  highlighted,
}: {
  path: string;
  content: string;
  highlighted: boolean;
}) {
  const language = codeLanguageForPath(path);
  if (highlighted && language && language !== "markdown") {
    return (
      <div className="min-w-0 [&_div]:!overflow-visible [&_pre]:!h-auto [&_pre]:!max-h-none [&_pre]:!overflow-visible">
        <CodeBlock code={content} language={language} showLineNumbers className="overflow-visible">
          <CodeBlockCopyButton />
        </CodeBlock>
      </div>
    );
  }
  return (
    <pre className="overflow-visible whitespace-pre-wrap rounded-xl border border-border bg-muted/30 p-4 font-mono text-[13px] leading-relaxed">
      {content}
    </pre>
  );
}
